import { useEffect, useState } from 'react';
import { MAX_BEST_OF, MIN_BEST_OF } from '@aiscoreboard/contracts';
import { useT } from '../lib/i18n';

interface Props {
  value: number;
  onChange: (value: number) => void;
}

const clamp = (n: number): number => Math.min(MAX_BEST_OF, Math.max(MIN_BEST_OF, n));

/**
 * Frame-count stepper.
 *
 * Even lengths are allowed on purpose: league and club formats often play a
 * fixed number of frames rather than a race. Typing is supported because
 * stepping from 1 to 19 is fifteen taps too many.
 */
export function FrameStepper({ value, onChange }: Props) {
  const t = useT();
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (raw: string): void => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(value));
      return;
    }
    onChange(clamp(parsed));
  };

  return (
    <div className="flex w-full flex-col items-center gap-1.5">
      <span className="label mb-0 text-center">{t('frames.label')}</span>

      <div className="flex w-full items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= MIN_BEST_OF}
          aria-label={t('frames.fewer')}
          className="step-btn h-11 w-11 text-xl"
        >
          −
        </button>

        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={t('frames.count')}
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className="field w-16 shrink-0 px-2 text-center text-2xl num-lg"
        />

        <button
          type="button"
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= MAX_BEST_OF}
          aria-label={t('frames.more')}
          className="step-btn h-11 w-11 text-xl"
        >
          +
        </button>
      </div>

    </div>
  );
}
